import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import { Loading, EmptyState, PageHeader } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen } from "lucide-react";

export default function Courses() {
  const [params, setParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const category = params.get("category") || "all";
  const course_type = params.get("course_type") || "all";
  const sort = params.get("sort") || "popular";

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v && v !== "all") p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (category !== "all") sp.set("category", category);
    if (course_type !== "all") sp.set("course_type", course_type);
    sp.set("sort", sort);
    const { data } = await api.get(`/courses?${sp.toString()}`);
    setCourses(data);
    setLoading(false);
  }, [q, category, course_type, sort]);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 min-h-[60vh]" data-testid="courses-page">
      <PageHeader title={q ? `Results for "${q}"` : "All Courses"} subtitle={`${courses.length} courses found`} />
      <div className="flex flex-wrap gap-3 mb-8 items-center">
        <SlidersHorizontal size={18} className="text-muted-foreground" />
        <Select value={category} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={course_type} onValueChange={(v) => setParam("course_type", v)}>
          <SelectTrigger className="w-36" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="recorded">Recorded</SelectItem>
            <SelectItem value="live">Live</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger className="w-44" data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="rating">Highest Rated</SelectItem>
            <SelectItem value="price_low">Price: Low to High</SelectItem>
            <SelectItem value="price_high">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loading /> : courses.length === 0 ? (
        <EmptyState icon={BookOpen} title="No courses found" subtitle="Try adjusting your filters or search terms." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {courses.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      )}
    </div>
  );
}
